class CartDrawerV2 extends HTMLElement {
  constructor() {
    super();
    this.addEventListener('keyup', (event) => event.code === 'Escape' && this.close());
    this.querySelector('[data-cart-drawer-v2-overlay]')?.addEventListener('click', () => this.close());
    this.querySelector('[data-cart-drawer-v2-close]')?.addEventListener('click', () => this.close());
    this.bindHeaderTriggers();
  }

  connectedCallback() {
    this.cartUpdateUnsubscriber = subscribe(PUB_SUB_EVENTS.cartUpdate, (event) => {
      if (event?.source === 'cart-drawer-v2') return;
      this.onCartUpdate(event);
    });
  }

  disconnectedCallback() {
    if (this.cartUpdateUnsubscriber) this.cartUpdateUnsubscriber();
  }

  bindHeaderTriggers() {
    const selector =
      '[data-header-v2-cart-trigger], [aria-controls="site-cart-sidebar"], a[href="/cart"], a[href="/cart/"]';
    document.addEventListener('click', (event) => {
      const trigger = event.target.closest(selector);
      if (!trigger || document.body.classList.contains('template-cart')) return;
      event.preventDefault();
      event.stopPropagation();
      this.open();
    }, { capture: true });
  }

  open() {
    this.setAttribute('open', '');
    document.body.classList.add('cart-drawer-v2-open');
  }

  close() {
    this.removeAttribute('open');
    document.body.classList.remove('cart-drawer-v2-open');
  }

  onCartUpdate(event) {
    if (!event?.cartData?.sections) return;
    this.renderContents(event.cartData);
    this.open();
  }

  renderContents(parsedState) {
    this.classList.toggle('cart-is-empty', parsedState.item_count === 0);

    const drawerHtml = parsedState.sections?.['cart-drawer-v2'];
    if (drawerHtml) {
      const drawerDoc = new DOMParser().parseFromString(drawerHtml, 'text/html');
      this.replaceInner('.cart-drawer-v2__body', drawerDoc);
      this.replaceInner('[data-cart-drawer-footer]', drawerDoc);
    }

    const headerSources = ['header', 'header-v2']
      .map((id) => parsedState.sections?.[id])
      .filter(Boolean)
      .map((html) => new DOMParser().parseFromString(html, 'text/html'));
    this.syncHeaderText('[data-header-cart-count]', headerSources);
    this.syncHeaderText('[data-header-cart-total]', headerSources);
  }

  syncHeaderText(selector, sourceDocs) {
    for (const doc of sourceDocs) {
      const next = doc.querySelector(selector);
      if (!next) continue;
      document.querySelectorAll(selector).forEach((node) => {
        node.textContent = next.textContent;
      });
      return;
    }
  }

  replaceInner(selector, sourceDoc) {
    const target = this.querySelector(selector);
    const source = sourceDoc.querySelector(selector);
    if (target && source) target.innerHTML = source.innerHTML;
  }

  updateQuantity(line, quantity) {
    if (!line) return;
    this.enableLoading();

    const body = JSON.stringify({
      id: line,
      quantity,
      sections: 'cart-drawer-v2,header-v2,header',
      sections_url: window.location.pathname,
    });

    fetch('/cart/change.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/javascript' },
      body,
    })
      .then((response) => response.json())
      .then((parsedState) => {
        if (parsedState.errors) {
          this.showError(parsedState.description || parsedState.message);
          publish(PUB_SUB_EVENTS.cartError, { source: 'cart-drawer-v2', message: parsedState.description });
          return;
        }
        this.hideError();
        this.renderContents(parsedState);
        publish(PUB_SUB_EVENTS.cartUpdate, { source: 'cart-drawer-v2', cartData: parsedState });
      })
      .catch(() => this.showError('Could not update cart. Please try again.'))
      .finally(() => this.disableLoading());
  }

  enableLoading() {
    this.setAttribute('aria-busy', 'true');
    this.querySelector('[data-cart-drawer-loading]')?.removeAttribute('hidden');
  }

  disableLoading() {
    this.removeAttribute('aria-busy');
    this.querySelector('[data-cart-drawer-loading]')?.setAttribute('hidden', '');
  }

  showError(message) {
    const slot = this.querySelector('[data-cart-drawer-error]');
    if (!slot) return;
    slot.textContent = message || 'Could not update cart.';
    slot.removeAttribute('hidden');
  }

  hideError() {
    const slot = this.querySelector('[data-cart-drawer-error]');
    if (!slot) return;
    slot.textContent = '';
    slot.setAttribute('hidden', '');
  }
}

if (!customElements.get('cart-drawer-v2')) customElements.define('cart-drawer-v2', CartDrawerV2);
